import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { AdminPractice, fetchAllPractices } from '../../services/practices';
import { useUser } from '../UserProvider';

export type PracticeListItemType = AdminPractice;

interface PracticesListContextType {
    practices: AdminPractice[];
    isLoading: boolean;
    error: string | null;
    refresh: (force?: boolean) => Promise<void>;
    reset: () => void;
}

const Ctx = createContext<PracticesListContextType | undefined>(undefined);

export function usePracticesList(): PracticesListContextType {
    const context = useContext(Ctx);
    if (context === undefined) {
        throw new Error('usePracticesList must be used within a PracticesListProvider');
    }
    return context;
}

export function PracticesListProvider({ children }: { children: ReactNode }) {
    const { user } = useUser();
    const isAdmin = !!user?.database.data.isAdmin;

    const {
        data: practices = [],
        isLoading,
        error: queryError,
        refetch
    } = useQuery({
        queryKey: ['practices', 'all'],
        queryFn: fetchAllPractices,
        enabled: isAdmin,
        staleTime: 15 * 60 * 1000
    });

    const error = queryError
        ? queryError instanceof Error
            ? queryError.message
            : 'Failed to fetch practices'
        : null;

    const refresh = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const reset = useCallback(() => {
        refetch();
    }, [refetch]);

    const value = useMemo<PracticesListContextType>(
        () => ({ practices, isLoading, error, refresh, reset }),
        [practices, isLoading, error, refresh, reset]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
